import type { HTMLAttributes, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import type { OrderStatus } from '@avh/shared';

export type Tone = 'info' | 'success' | 'warning' | 'error';

const TONES: Record<Tone, string> = {
  info: 'border-brand-600 bg-brand-50 text-brand-950',
  success: 'border-brand-700 bg-brand-50 text-brand-950',
  warning: 'border-sun-300 bg-sun-100 text-ink',
  error: 'border-accent-600 bg-accent-50 text-accent-700',
};

interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  tone?: Tone;
  title?: string;
}

export function Alert({ tone = 'info', title, className, children, ...rest }: AlertProps) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={twMerge('rounded-md border-l-4 px-4 py-3 text-sm', TONES[tone], className)}
      {...rest}
    >
      {title ? <p className="font-semibold">{title}</p> : null}
      {children}
    </div>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 py-8 text-muted" role="status">
      <span className="size-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function Card({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={twMerge('rounded-lg bg-white p-5 shadow-sm ring-1 ring-black/5 sm:p-6', className)} {...rest} />;
}

export function PageHeading({ eyebrow, title, children }: { eyebrow?: string; title: string; children?: ReactNode }) {
  return (
    <div className="mb-6">
      {eyebrow ? <p className="text-sm font-semibold tracking-wide text-brand-600 uppercase">{eyebrow}</p> : null}
      <h1 className="text-2xl font-bold text-brand-900 sm:text-3xl">{title}</h1>
      {children ? <div className="mt-2 text-muted">{children}</div> : null}
    </div>
  );
}

/** Kleines Status-Etikett: immer Symbol + Text, nie nur über die Farbe. */
export function Badge({ icon, className, children }: { icon: string; className: string; children: ReactNode }) {
  return (
    <span className={twMerge('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ring-1', className)}>
      <span aria-hidden="true">{icon}</span>
      {children}
    </span>
  );
}

const STATUS_STYLES: Record<OrderStatus, string> = {
  reserved: 'bg-sun-100 text-ink ring-sun-300',
  paid: 'bg-brand-50 text-brand-900 ring-brand-600',
  cancelled: 'bg-neutral-100 text-muted ring-neutral-300',
  expired: 'bg-accent-50 text-accent-700 ring-accent-500',
};

const STATUS_ICONS: Record<OrderStatus, string> = {
  reserved: '◔',
  paid: '✓',
  cancelled: '✕',
  expired: '!',
};

/** Status einer Bestellung. */
export function StatusBadge({ status, children }: { status: OrderStatus; children: ReactNode }) {
  return (
    <Badge icon={STATUS_ICONS[status]} className={STATUS_STYLES[status]}>
      {children}
    </Badge>
  );
}
